import { useEffect, useState } from 'react'
import { IPVPNavigation } from '@/components/Navbar'
import { useAuth } from '@/context/AuthContext'
import { getUserDisplayName, getUserInitials } from '@/utils/user-display'
import { adminApi } from '@/utils/api'
import type { AdminWaitlistEntry, AdminWaitlistResponse } from '@/types/admin'

export default function AdminWaitlistPage() {
  const { user } = useAuth()
  const userName = getUserDisplayName(user)
  const userInitials = getUserInitials(user)
  // const [overview, setOverview] = useState<AdminWaitlistOverview | null>(null)
  const [entries, setEntries] = useState<AdminWaitlistEntry[]>([])
  const [, setTotalEntries] = useState(0)
  const [error, setError] = useState<string | null>(null)
  const [loading, setLoading] = useState(true)
  const [updatingId, setUpdatingId] = useState<number | null>(null)

  const fetchData = async () => {
    try {
      setError(null)
      setLoading(true)
      const [entriesRes] = await Promise.all([
        // adminApi.waitlistOverview(),
        adminApi.waitlistEntries({ pageSize: 50 })
      ])
      // setOverview((overviewRes as { data?: AdminWaitlistOverview }).data || (overviewRes as AdminWaitlistOverview))
      const entriesData = (entriesRes as { data?: AdminWaitlistResponse | AdminWaitlistEntry[] }).data
      if (entriesData && 'items' in entriesData) {
        // Paginated response
        setEntries(entriesData.items)
        setTotalEntries(entriesData.total)
      } else if (Array.isArray(entriesData)) {
        // Direct array response
        setEntries(entriesData)
        setTotalEntries(entriesData.length)
      } else {
        setEntries([])
        setTotalEntries(0)
      }
    } catch (err) {
      setError((err as Error)?.message || 'Failed to load waitlist')
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    void fetchData()
  }, [])

  const handleConvert = async (entry: AdminWaitlistEntry) => {
    if (updatingId) return
    setUpdatingId(entry.id)
    try {
      await adminApi.updateWaitlistStatus(entry.id, 'converted')
      await fetchData()
    } catch (err) {
      setError((err as Error)?.message || 'Failed to convert user')
    } finally {
      setUpdatingId(null)
    }
  }

  return (
    <div className='w-full min-h-screen bg-[#F8F9FA] px-4 py-6 md:px-6 lg:px-8 space-y-6'>
      <div className='bg-white rounded-lg shadow-sm p-4'>
        <IPVPNavigation
          title=' Waitlist'
          infoText='Waitlist overview with conversions and statuses.'
          showLocation={false}
          userName={userName}
          userInitials={userInitials}
          only
        />
      </div>

      {/* <div className='grid grid-cols-1 md:grid-cols-3 gap-4'>
        <StatCard
          title='Total Waitlist'
          value={loading ? '...' : (totalEntries || overview?.total || 0).toLocaleString()}
          subtitle='All signups'
          trend=''
        />
        <StatCard
          title='Converted'
          value={loading ? '...' : (overview?.converted ?? 0).toLocaleString()}
          subtitle='Joined platform'
          trend={overview?.conversionRate ? `${Math.round(overview.conversionRate)}% conversion` : ''}
        />
        <StatCard
          title='Pending'
          value={loading ? '...' : (overview?.pending ?? 0).toLocaleString()}
          subtitle='Need outreach'
          trend=''
        />
      </div> */}

      <div className='bg-white rounded-xl border border-gray-100 p-4 shadow-sm'>
        <h3 className='text-lg font-semibold text-gray-900 mb-4'>Recent Signups</h3>
        <div className='grid grid-cols-5 gap-2 bg-gray-50 px-4 py-2 text-xs font-semibold text-gray-700 text-start'>
          <span>Name</span>
          <span>Email</span>
          <span>Phone</span>
          <span>Status</span>
          <span className='text-right'>Action</span>
        </div>
        {loading && <p className='text-sm text-gray-600 p-4'>Loading waitlist entries...</p>}
        {!loading && entries.length === 0 && <p className='text-sm text-gray-600 p-4'>No waitlist entries found.</p>}
        {!loading && entries.length > 0 && entries.map((entry) => (
          <div key={entry.id} className='grid grid-cols-5 gap-2 px-4 py-3 text-sm text-gray-800 border-t border-gray-100 items-center text-start'>
            <span>{entry.firstName} {entry.lastName}</span>
            <span>{entry.email}</span>
            <span>{entry.phoneNumber || '—'}</span>
            <span className={
              entry.status === 'converted' ? 'text-green-600 font-semibold capitalize' :
              entry.status === 'contacted' ? 'text-blue-600 font-semibold capitalize' :
              'text-orange-600 font-semibold capitalize'
            }>
              {entry.status}
            </span>
            <div className='flex justify-end gap-2 text-xs'>
              {entry.status !== 'converted' && (
                <button 
                  className='px-3 py-1 rounded-md bg-orange-50 text-orange-600 hover:bg-orange-100 disabled:opacity-50 disabled:cursor-not-allowed'
                  onClick={() => handleConvert(entry)}
                  disabled={updatingId === entry.id}
                >
                  {updatingId === entry.id ? 'Converting...' : 'Convert'}
                </button>
              )}
              <button className='px-3 py-1 rounded-md bg-gray-100 hover:bg-gray-200'>Message</button>
            </div>
          </div>
        ))}
        <p className='text-xs text-gray-500 mt-3'>
          Showing recent waitlist signups with conversion actions.
        </p>
      </div>
      {error && <p className='text-sm text-red-600'>{error}</p>}
    </div>
  )
}
